import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  readApplySession,
  saveResumeStageAfterBasic,
  markHubStage,
  applicationFormPath,
} from '../../lib/screeningApplySession';
import { reportDiag, reportPageOpen } from '../../lib/candidateDiag';
import { usePreventBack } from '../../hooks/usePreventBack';
import { useScreeningLeaveWarning } from '../../hooks/useScreeningLeaveWarning';
import AnimatedModal from '../../components/ui/AnimatedModal';
import LeaveScreeningModal from '../../components/ui/LeaveScreeningModal';
import { useScreeningExpiryRedirect } from '../../hooks/useScreeningExpiryRedirect';
import { isProdHost } from '../../lib/isProdHost';
import { Listbox, Transition } from '@headlessui/react';
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  ArrowRightIcon,
  ClockIcon,
  MapPinIcon,
  BanknotesIcon,
  SparklesIcon,
  CheckIcon,
  LinkIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  ChatBubbleBottomCenterTextIcon,
  ChevronDownIcon,
  InformationCircleIcon,
  MicrophoneIcon,
  VideoCameraIcon,
  AcademicCapIcon,
  ShieldCheckIcon,
  WifiIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';

import DeviceCheckModal from '../../components/ui/DeviceCheckModal';
import ConsentNoticeModal from '../../components/consent/ConsentNoticeModal';
import { markDeviceAsChecked } from '../../utils/deviceCheckMemory';
import { getStoreCodeRule, normaliseStoreFormat } from '../../utils/storeFormat';
import { useCandidateLanguage } from '../../i18n/candidate';

/** Fills the {name} placeholders of a (translated) sentence with React nodes. */
const withNodes = (text, nodes) =>
  text.split(/\{(\w+)\}/).map((part, i) => (i % 2 ? <React.Fragment key={i}>{nodes[part]}</React.Fragment> : part));

// Legal text: deliberately left in English for every journey, the French one included (the
// consent log hashes this exact wording), so it is not passed through t().
/** Full privacy policy text shown in the consent modal. Mirrors the approved
 * "TalentCrux Privacy Policy" document (Version 1.0, effective 06 August 2026).
 * Section 18 is the operative document-collection consent and is not part of
 * that document — keep it appended when the policy text is next refreshed. */
const PRIVACY_POLICY_CONTENT = `Company Name: TalentCrux
Document Name: Privacy Policy
Version: 1.0
Effective Date: 06 August 2026

Indara Technologies Private Limited ("TalentCrux", "We", "Our" or "Us") operates the TalentCrux Platform, an AI-powered recruitment and hiring solution to support candidate applications, interviews, assessments, and related hiring activities. TalentCrux is committed to processing Personal Data in a lawful and responsible manner, in accordance with Applicable Law.

This Privacy Policy states how TalentCrux collects, uses, shares, and otherwise processes Personal Data in connection with your interaction with Our Website, Platform, and related services.

This Privacy Policy applies to all individuals whose Personal Data is processed by TalentCrux, including Customers, Candidates, Employees, Vendors, Website Visitors, and other individuals who interact with TalentCrux.

1. Definitions
Unless the context otherwise requires, the following terms shall have the meanings assigned to them below:

• Applicable Law: means all laws, rules, regulations, notifications, directions, orders, and judgments, including the Digital Personal Data Protection Act, 2023 and the rules made thereunder.
• Candidate: means an individual who applies for employment or participates in a recruitment process through the Platform.
• Customer: means an organisation that subscribes to the TalentCrux Platform.
• Data Fiduciary: means any person who alone or in conjunction with other persons determines the purpose and means of processing of personal data.
• Data Principal: means the individual to whom the personal data relates.
• Data Processor: means any person who processes personal data on behalf of a Data Fiduciary.
• Employee: means any individual who is employed or engaged by TalentCrux, whether on a permanent, contractual, or internship basis.
• Personal Data: means any data about an individual who is identifiable by or in relation to such data.
• Personal Data Breach: means any unauthorised processing of personal data or accidental disclosure, acquisition, sharing, use, alteration, destruction or loss of access to personal data, that compromises the confidentiality, integrity or availability of Personal Data.
• Platform: means the TalentCrux platform, candidate application interface, and related services made available by TalentCrux.
• Website: means the TalentCrux website available at "www.talentcrux.ai".

2. Categories of Personal Data We Collect
The categories of Personal Data that We process depend upon how you interact with TalentCrux. TalentCrux processes only such Personal Data as is necessary for the purposes described in this Privacy Policy. The categories of Personal Data collected may vary depending upon the nature of your interaction with TalentCrux, and the requirements of Applicable Law.

We may process the following categories of Personal Data:

Candidates (TalentCrux does not require Candidates to submit any specific category of Personal Data unless such information is requested by the Customer)
• Resume
• Audio and video recording during the interview
• Aadhaar
• PAN Card
• Employment history
• Assessments
• Transcripts
• AI-generated reports

Customers
• Name
• Contact number
• Email
• Designation

Employees
• Name
• Address
• Age
• Resume
• Bank details
• Aadhaar Card

Vendors
• Name
• Email
• Contact number

Website Visitors
• Cookies

3. Duties of Data Principals
While interacting with the Platform or Website or exercising their rights under Applicable Law, all Data Principals are expected to comply with the duties prescribed under Applicable Law. Accordingly, Data Principals shall:

• not impersonate another person while providing their Personal Data for any specified purpose
• not suppress any material information while providing Personal Data for any document, unique identifier, proof of identity, or proof of address
• not register any false or frivolous grievance or complaint with TalentCrux or the Data Protection Board ("DPB")
• furnish only such information as is verifiably authentic while exercising any right to correction, completion, updating, or erasure of Personal Data under Applicable Law.

4. Our Role in Processing Your Personal Data
Depending on the nature of your interaction with TalentCrux, We may process your Personal Data either as a Data Fiduciary or as a Data Processor.

Where TalentCrux determines the purpose and means of processing Personal Data, TalentCrux acts as the Data Fiduciary.

Where Customers use the Platform for recruitment or other purposes and TalentCrux processes Personal Data solely on their behalf, TalentCrux acts as a Data Processor.

5. Purposes of Processing
Depending on your relationship with TalentCrux, We may process Personal Data for one or more of the following purposes:

• receiving and processing candidate applications
• conducting interviews, assessments, evaluations, and related hiring activities on behalf of Customers
• providing, operating, maintaining, securing, and improving the Platform, Website and related services
• creating and administering Customer accounts
• responding to enquiries, requests, and other communications
• onboarding, administering, and managing employees
• processing invoices, payments, and other transactions where applicable
• maintaining the availability, performance, and security of the Website and Platform
• detecting, investigating, preventing, and responding to unauthorised access, cybersecurity incidents, and other security risks, and
• complying with Applicable Law

6. Lawful Basis of Processing
TalentCrux processes Personal Data with the consent of the Data Principal where such consent is required, for specified legitimate uses recognised under Applicable Law, and where such processing is otherwise permitted under Applicable Law.

7. Sources of Personal Data
TalentCrux collects Personal Data directly from you, through your use of the Website or Platform, from Customers, Employers, Vendors, or other individuals interacting with TalentCrux, as per the Applicable Law.

8. Rights of Data Principals
Subject to Applicable Law, Data Principals may exercise the following rights in relation to their Personal Data:

• the right to seek information regarding the processing of their Personal Data
• the right to request correction, completion, updating, or erasure of Personal Data, where applicable
• the right to withdraw consent where processing is based on consent
• the right to grievance redressal, and,
• the right to nominate another individual to exercise such rights in accordance with Applicable Law

Requests relating to Personal Data processed by TalentCrux as a Data Processor may be redirected to the relevant Data Fiduciary, as appropriate.

9. AI-Assisted Processing
TalentCrux provides AI-powered recruitment tools that assist Customers during various stages of the recruitment process.

The Platform incorporates artificial intelligence to support various stages of the recruitment process. Depending on the services used by the Customer, these technologies may include large language models, generative AI models, automated transcription tools, and other AI-assisted technologies.

Depending on the recruitment, AI-assisted technologies may be used to analyse resumes and application information, conduct AI voice or video interviews, generate interview transcripts, summarise interview responses, evaluate assessment responses, generate interview reports and candidate insights, and assist recruiters in reviewing candidate information more efficiently.

These AI-assisted features are intended to support the recruitment process and improve operational efficiency. They do not independently make employment or hiring decisions. Final recruitment decisions, including candidate shortlisting, selection, rejection, or employment offers, are made solely by the Customer.

10. Sharing of Personal Data
TalentCrux treats your Personal Data as confidential and shares it only with your consent for the specified purpose, or where such sharing is required under Applicable Law.

TalentCrux may engage trusted third-party service providers to support hosting, cloud infrastructure, security, artificial intelligence, and other functions relating to the Platform. Such service providers process Personal Data only to the extent necessary for providing the relevant services and are subject to appropriate contractual obligations.

TalentCrux may share Personal Data with the following categories of recipients, or such other recipients as may be required under Applicable Law, to the extent necessary for the specified purpose or otherwise permitted under Applicable Law:

Customers:
Where TalentCrux provides services as a Data Processor, Personal Data may be shared with the relevant Customer to enable recruitment, hiring, or other services requested by the Customer.

Service Providers:
TalentCrux may engage third-party service providers for hosting, cloud infrastructure, artificial intelligence, security, and other services.

11. Cross Border Transfer of Personal Data
Some of the service providers engaged by TalentCrux may process Personal Data outside India where necessary for providing the services necessary for the operation, maintenance, hosting, support, security, and improvement of the Platform.

Before transferring Personal Data outside India, TalentCrux undertakes due diligence in selecting its service providers and ensures that such service providers implement appropriate contractual, organisational, and technical safeguards for the protection of Personal Data.

Where required under Applicable Law, TalentCrux seeks to ensure that recipients of Personal Data outside India implement a level of data protection substantially comparable to that maintained by TalentCrux.

TalentCrux remains responsible for Personal Data processed on its behalf by Data Processors to the extent required under Applicable Law. Where Personal Data is transferred outside India, TalentCrux ensures that such processing is carried out in accordance with the Applicable Law and applicable contractual obligations.

12. Data Retention
TalentCrux retains your Personal Data only for as long as necessary to fulfil the purposes described in this Privacy Policy for which it was collected, unless a longer retention period is required under Applicable Law.

Where Personal Data is no longer required, TalentCrux will erase such Personal Data in accordance with the Applicable Law.

Where Personal Data is processed on behalf of a Customer, the applicable retention period may also be determined by the Customer's instructions, subject to Applicable Law.

13. Security of Your Personal Data
TalentCrux undertakes appropriate technical and organisational measures to protect Personal Data in its possession or under its control, including where such Processing is carried out on its behalf by a Data Processor.

Such security safeguards and measures are designed to prevent Personal Data breaches and to safeguard Personal Data against unauthorised access, disclosure or loss. Such security safeguards include, at the minimum:

• securing of Personal Data through encryption
• measures to control access to the computer resources used by TalentCrux
• logs and monitoring of personal data, for detection of unauthorised access, its investigation and remediation to prevent recurrence
• data-backups
• retention of access logs and Personal Data as per the Applicable Law
• ensuring provision in the contract entered into with a Data Processor, wherever applicable, for taking reasonable security safeguards

14. Data Breach
In the event We become aware of a Personal Data Breach or a cyber security incident, We take immediate steps to contain and mitigate such event and notify the relevant authorities as required under the Applicable Law.

We also notify the affected Data Principal of the Personal Data Breach in such form and manner as may be required under Applicable Law, providing sufficient details of the nature of the breach and the steps being taken to mitigate its effects.

15. Children's Privacy
The Platform is intended for recruitment and employment-related purposes. Accordingly, the Website and Platform are not intended for use by individuals below the age of eighteen (18) years.

16. Grievance Redressal
If you have any questions, concerns, or grievances regarding this Privacy Policy or the processing of your Personal Data, you may contact Us at:

Email: support@talentcrux.ai

TalentCrux will acknowledge and address grievances within the timelines prescribed under Applicable Law.

17. Changes to this Privacy Policy
TalentCrux may update this Privacy Policy from time to time to reflect changes as per the Applicable Law.

The updated version of this Privacy Policy will be made available on the Website and, where appropriate, through the Platform. The revised Privacy Policy shall become effective from the Effective Date specified therein and shall supersede all previous versions of this Privacy Policy.

This Privacy Policy was last modified on: 06 August 2026

18. Document Collection & Processing
I consent to the secure collection, processing, and verification of my submitted documents by TalentCrux on behalf of the recruiting organization in accordance with the Indian DPDP Act, 2023.`;

// Bumped to v2 alongside the Version 1.0 policy text above. Candidates who
// accepted the earlier text stay recorded as v1 on their screening record.
// v3: the checkbox now names and links the TalentCrux Terms of Use as well as the
// Privacy Policy (the policy text itself, and so its hash, is unchanged).
const SCREENING_CONSENT_VERSION = 'v3';

/* The org's own consent notice (e.g. Ajanta's): the consent log id is kept for this browser
 * tab, per screening and notice version, so a refresh does not ask again but a new version of
 * the text does. sessionStorage can be unavailable; then we simply ask again. */
const noticeConsentKey = (screeningId, notice) => `tc_notice_consent:${screeningId}:${notice.noticeId}@${notice.version}`;
function readNoticeConsent(screeningId, notice) {
  try { return window.sessionStorage.getItem(noticeConsentKey(screeningId, notice)) || ''; } catch { return ''; }
}
function saveNoticeConsent(screeningId, notice, logId) {
  try {
    if (logId) window.sessionStorage.setItem(noticeConsentKey(screeningId, notice), logId);
    else window.sessionStorage.removeItem(noticeConsentKey(screeningId, notice));
  } catch { /* storage blocked */ }
}

/** SHA-256 hex of the policy text the candidate was shown — stored on the DPDP
 * consent log as proof of the exact wording. crypto.subtle only exists in
 * secure contexts (https / localhost); without it the log is still written,
 * just with no hash. */
async function hashPolicyText(text) {
  try {
    if (!window.crypto?.subtle || typeof TextEncoder === 'undefined') return '';
    const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return '';
  }
}

/* ─── Step metadata for the process timeline ─── */
const getStepMeta = (interviewType) => ({
  'Basic details': { icon: UserIcon, desc: '', gradient: 'from-blue-500 to-indigo-600' },
  'Resume upload': { icon: DocumentTextIcon, desc: '', gradient: 'from-electric-blue to-indigo-600' },
  'Assessment': { icon: AcademicCapIcon, desc: '', gradient: 'from-electric-blue to-indigo-600' },
  'AI interview': {
    icon: interviewType === 'video' ? VideoCameraIcon : MicrophoneIcon,
    desc: '',
    gradient: 'from-orange-500 to-rose-600',
  },
  'Document upload': { icon: DocumentTextIcon, desc: '', gradient: 'from-electric-blue to-indigo-600' },
  'Application form': { icon: ClipboardDocumentListIcon, desc: '', gradient: 'from-electric-blue to-indigo-600' },
});

function getInterviewStepLabel(interviewType) {
  if (interviewType === 'video') return 'AI video interview';
  if (interviewType === 'voice' || interviewType === 'audio') return 'AI voice interview';
  return 'AI interview';
}

/* ─── Skills tooltip ─── */
const SkillsTooltip = ({ skills }) => {
  const [show, setShow] = useState(false);
  const ref = useRef(null);
  if (!skills || skills.length === 0) return null;
  return (
    <span className="relative inline-flex items-center gap-1 cursor-help" ref={ref}
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}
      onFocus={() => setShow(true)} onBlur={() => setShow(false)}
      tabIndex={0} aria-label={`Required skills: ${skills.join(', ')}`}>
      <InformationCircleIcon className="h-4 w-4 text-gray-400" />
      <span className="text-xs text-gray-500 font-medium">Skills</span>
      {show && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-max max-w-xs px-3 py-2 rounded-lg bg-gray-900 text-white text-xs leading-relaxed shadow-lg pointer-events-none animate-fade-in">
          {skills.join(' · ')}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </span>
      )}
    </span>
  );
};

const ScreeningApply = () => {
  const { screeningId } = useParams();
  const t = useCandidateLanguage({ screeningId });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailFromQuery = searchParams.get('email') || '';
  const nameFromQuery = searchParams.get('name') || '';
  const phoneFromQuery = searchParams.get('phone') || '';

  const [showLeaveModal, setShowLeaveModal] = useState(false);
  usePreventBack(true, { onBackAttempt: useCallback(() => setShowLeaveModal(true), []) });
  useScreeningLeaveWarning(!!screeningId);
  // Expiry redirect disabled on the initial apply form — no email/deadline to track yet.
  // The hub/resume pages enable it with the candidate's email after screening starts.
  useScreeningExpiryRedirect({ screeningId, enabled: false });

  // Capability/context baseline for the first step of the journey. Recorded once
  // per page view so any later failure on this device can be compared against a
  // device where the journey completes.
  useEffect(() => {
    if (!screeningId) return;
    reportPageOpen(screeningId, 'details', {});
  }, [screeningId]);
  const [applyInfo, setApplyInfo] = useState(null);
  const [applyInfoLoading, setApplyInfoLoading] = useState(true);
  const [linkInactive, setLinkInactive] = useState(false);
  const [step, setStep] = useState('form');
  const [journeyStage, setJourneyStage] = useState('basic');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [thankYouData, setThankYouData] = useState(null);
  const [aboutRoleExpanded, setAboutRoleExpanded] = useState(false);
  const [jobDescriptionModalOpen, setJobDescriptionModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [canAcceptPrivacy, setCanAcceptPrivacy] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [showDeviceCheckModal, setShowDeviceCheckModal] = useState(false);
  // Hiring org's own DPDP consent notice — null while loading or when the org has none.
  const [consentNotice, setConsentNotice] = useState(null);
  const [noticeConsentLogId, setNoticeConsentLogId] = useState('');
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [noticeSubmitting, setNoticeSubmitting] = useState(false);
  const [noticeError, setNoticeError] = useState('');
  const [form, setForm] = useState({
    name: nameFromQuery || '',
    email: emailFromQuery || '',
    phone: phoneFromQuery || '',
    consent: !!emailFromQuery,
    resume: null,
    customFields: {},
    storeId: '',
    cityId: '',
  });
  const [storeSearchQuery, setStoreSearchQuery] = useState('');
  const [citySearchQuery, setCitySearchQuery] = useState('');
  const [customFieldSearchQueries, setCustomFieldSearchQueries] = useState({});
  const resumePromptShownRef = useRef(false);
  // Pending/settled POST /consent for the latest "I Accept" click; resolves to
  // the consent log id (or null) which /start sends back to link the row.
  const consentLogPromiseRef = useRef(null);
  // The <input> is the reliable holder of the picked file. Samsung's Android 12
  // Chrome can leave the File kept in React state stale, so the form submits
  // with nothing attached while the filename is still shown on screen — read
  // from the element at submit instead of trusting form.resume.
  const resumeInputRef = useRef(null);
  const [selectedResumeName, setSelectedResumeName] = useState('');

  const isGoColors = useMemo(() => {
    return !!applyInfo?.companyName?.toLowerCase().includes('go colors');
  }, [applyInfo?.companyName]);



  useEffect(() => {
    let cancelled = false;
    const fetchApplyInfo = async () => {
      try {
        const params = emailFromQuery ? `?email=${encodeURIComponent(emailFromQuery)}` : '';
        const res = await axios.get(`/api/screenings/${screeningId}/apply-info${params}`);
        if (!cancelled) { setApplyInfo(res.data); setLinkInactive(false); }
      } catch (e) {
        if (!cancelled) {
          setApplyInfo(null);
          setLinkInactive(e.response?.status === 410 && e.response?.data?.code === 'LINK_INACTIVE');
        }
      } finally { if (!cancelled) setApplyInfoLoading(false); }
    };
    fetchApplyInfo();
    return () => { cancelled = true; };
  }, [screeningId]);

  const loadConsentNotice = useCallback(async () => {
    try {
      const res = await axios.get(`/api/screenings/${screeningId}/consent-notice`);
      const notice = res.data?.enabled ? res.data.notice : null;
      setConsentNotice(notice);
      setNoticeConsentLogId(notice ? readNoticeConsent(screeningId, notice) : '');
      // An invite link pre-ticks the TalentCrux box; with the org notice on, the candidate
      // ticks it themselves like everyone else — two separate, active agreements.
      if (notice) setForm((p) => ({ ...p, consent: false }));
    } catch (err) {
      // The server still refuses /start without the notice consent, so failing open here
      // cannot let a candidate through; it only means the pop-up is missing until a reload.
      console.error('[CONSENT_NOTICE] Failed to load the consent notice', {
        screeningId,
        httpStatus: err.response?.status,
      });
    }
  }, [screeningId]);

  useEffect(() => { loadConsentNotice(); }, [loadConsentNotice]);

  const handleNoticeAgree = async (language) => {
    if (!consentNotice) return;
    setNoticeSubmitting(true);
    setNoticeError('');
    try {
      const res = await axios.post(
        `/api/screenings/${screeningId}/consent`,
        { kind: 'fiduciary_notice', noticeId: consentNotice.noticeId, noticeVersion: consentNotice.version, language, ageConfirmed: true },
        { timeout: 15000 }
      );
      const logId = res.data?.logId || '';
      saveNoticeConsent(screeningId, consentNotice, logId);
      setNoticeConsentLogId(logId);
      setNoticeOpen(false);
    } catch (err) {
      if (err.response?.data?.code === 'NOTICE_OUTDATED') {
        await loadConsentNotice();
        setNoticeError(err.response.data.message);
      } else {
        setNoticeError(err.response?.data?.message || 'We could not record your consent. Please check your connection and try again.');
      }
    } finally {
      setNoticeSubmitting(false);
    }
  };

  useEffect(() => {
    if (applyInfo?.completed) {
      const thankYouParams = new URLSearchParams();
      if (emailFromQuery) thankYouParams.set('email', emailFromQuery);
      if (nameFromQuery) thankYouParams.set('name', nameFromQuery);
      if (applyInfo.rejected) thankYouParams.set('rejected', 'true');
      const qs = thankYouParams.toString();
      navigate(`/apply/${screeningId}/thank-you${qs ? `?${qs}` : ''}`, { replace: true });
    }
  }, [applyInfo, screeningId, navigate, emailFromQuery, nameFromQuery]);

  useEffect(() => {
    if (applyInfo?.existingCandidate) {
      const ageField = applyInfo.registrationFields?.find(
        (f) => f.fieldType === 'age' || f.label?.toLowerCase() === 'age'
      );
      const dobField = applyInfo.registrationFields?.find(
        (f) => f.fieldType === 'dob' || f.label?.toLowerCase() === 'dob' || f.label?.toLowerCase() === 'date of birth'
      );
      const genderField = applyInfo.registrationFields?.find(
        (f) => f.fieldType === 'gender' || f.label?.toLowerCase() === 'gender'
      );

      const newCustomFields = { ...(applyInfo.existingCandidate.customFields || {}) };
      if (ageField && applyInfo.existingCandidate.age) {
        newCustomFields[ageField.fieldId] = applyInfo.existingCandidate.age;
      }
      if (dobField && applyInfo.existingCandidate.dob) {
        try {
          const d = new Date(applyInfo.existingCandidate.dob);
          const dd = String(d.getUTCDate()).padStart(2, '0');
          const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
          const yyyy = d.getUTCFullYear();
          newCustomFields[dobField.fieldId] = `${dd}/${mm}/${yyyy}`;
        } catch (e) {
          // ignore invalid date
        }
      }
      if (genderField && applyInfo.existingCandidate.gender) {
        newCustomFields[genderField.fieldId] = applyInfo.existingCandidate.gender;
      }

      setForm((p) => ({
        ...p,
        name: p.name || applyInfo.existingCandidate.name || '',
        phone: p.phone || applyInfo.existingCandidate.phone || '',
        storeId: p.storeId || applyInfo.existingCandidate.storeId || '',
        cityId: p.cityId || applyInfo.existingCandidate.cityId || '',
        customFields: {
          ...newCustomFields,
          ...p.customFields,
        },
      }));
    }
  }, [applyInfo]);

  // The job's store format decides the code scheme the candidate types (1101 vs LF-PT-PV01).
  const storeCodeFormat = normaliseStoreFormat(applyInfo?.storeFormat);
  const storeCodeRule = getStoreCodeRule(storeCodeFormat);

  const filteredStores = useMemo(() => {
    const stores = applyInfo?.stores || [];
    const query = storeSearchQuery.trim().toLowerCase();
    if (!query) return stores;
    return stores.filter((s) => {
      const storeId = (s.storeId || '').toLowerCase();
      const name = (s.name || '').toLowerCase();
      const city = (s.city || '').toLowerCase();
      const state = (s.state || '').toLowerCase();
      return storeId.includes(query) || name.includes(query) || city.includes(query) || state.includes(query);
    });
  }, [applyInfo?.stores, storeSearchQuery]);

  const filteredCities = useMemo(() => {
    const cities = applyInfo?.cities || [];
    const query = citySearchQuery.trim().toLowerCase();
    if (!query) return cities;
    return cities.filter((c) => {
      const city = (c.city || '').toLowerCase();
      const state = (c.state || '').toLowerCase();
      const region = (c.region || '').toLowerCase();
      return city.includes(query) || state.includes(query) || region.includes(query);
    });
  }, [applyInfo?.cities, citySearchQuery]);

  useEffect(() => {
    if (applyInfoLoading || !applyInfo || linkInactive) return;
    if (resumePromptShownRef.current) return;
    const session = readApplySession(screeningId);
    if (!session || (session.stage !== 'resume' && session.stage !== 'hub')) return;
    resumePromptShownRef.current = true;

    const hubEmail = session.hubEmail || session.basicDetails?.email || '';
    const hubName = session.hubName || session.basicDetails?.name || '';
    const hubQs = new URLSearchParams();
    if (hubEmail) hubQs.set('email', hubEmail);
    if (hubName) hubQs.set('name', hubName);
    const hubSuffix = hubQs.toString() ? `?${hubQs.toString()}` : '';

    const isResume = session.stage === 'resume';
    const title = isResume ? 'Resume your application' : 'Continue your screening';
    const body = isResume
      ? 'You started this screening earlier. Continue from the resume and details step on this device.'
      : 'You can return to your screening hub to finish the assessment or interview.';

    toast.custom(
      (toastItem) => (
        <div className="pointer-events-auto flex max-w-md flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-xl">
          <div className="pr-6">
            <p className="text-sm font-semibold text-gray-900">{t(title)}</p>
            <p className="mt-1 text-xs text-gray-600 leading-relaxed">{t(body)}</p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              className="rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-100"
              onClick={() => toast.dismiss(toastItem.id)}
            >
              {t('Dismiss')}
            </button>
            <button
              type="button"
              className="rounded-lg bg-electric-blue px-4 py-1.5 text-xs font-bold text-white shadow-sm hover:bg-electric-blue-600"
              onClick={() => {
                toast.dismiss(toastItem.id);
                if (isResume) {
                  const s = readApplySession(screeningId);
                  const bd = s?.basicDetails;
                  navigate(`/apply/${screeningId}/resume`, {
                    state: bd ? { basicDetails: bd } : undefined,
                  });
                } else {
                  navigate(`/apply/${screeningId}/hub${hubSuffix}`);
                }
              }}
            >
              {t('Continue')}
            </button>
          </div>
        </div>
      ),
      { duration: 14000, position: 'top-center' }
    );
  }, [applyInfoLoading, applyInfo, linkInactive, screeningId, navigate, t]);

  const handleChange = (e) => {
    const { name, value, files, type, checked } = e.target;
    if (name.startsWith('custom_')) {
      const fieldName = name.replace('custom_', '');
      let finalValue = value;
      const fieldConfig = applyInfo?.registrationFields?.find(f => f.fieldId === fieldName);
      if (fieldConfig && (fieldConfig.fieldType === 'dob' || fieldConfig.label?.toLowerCase() === 'dob' || fieldConfig.label?.toLowerCase() === 'date of birth')) {
        let val = value.replace(/\D/g, '');
        if (val.length > 2) val = val.slice(0, 2) + '/' + val.slice(2);
        if (val.length > 5) val = val.slice(0, 5) + '/' + val.slice(5, 9);
        finalValue = val.slice(0, 10);
      }
      setForm((p) => ({ ...p, customFields: { ...p.customFields, [fieldName]: type === 'checkbox' ? checked : finalValue } }));
      setError('');
      return;
    }
    if (files) {
      setForm((p) => ({ ...p, [name]: files[0] || null }));
      // Keep the name as a plain string too: a String survives even when the
      // File reference in state goes stale, so the candidate keeps seeing what
      // they picked.
      if (name === 'resume') setSelectedResumeName(files[0]?.name || '');
    }
    else if (type === 'checkbox') {
      if (name === 'consent') {
        if (checked) { setCanAcceptPrivacy(false); setPrivacyModalOpen(true); return; }
        setForm((p) => ({ ...p, [name]: false })); setError(''); return;
      }
      setForm((p) => ({ ...p, [name]: checked }));
    } else {
      if (name === 'phone') {
        const numbersOnly = value.replace(/\D/g, '').slice(0, 10);
        setForm((p) => ({ ...p, [name]: numbersOnly }));
      } else {
        setForm((p) => ({ ...p, [name]: value }));
      }
    }
    setError('');
  };

  // DPDP consent log: record the "I Accept" click server-side (IP, device and
  // server time are captured there). Never blocks the candidate — acceptance
  // proceeds even if the request fails, and the promise always resolves.
  const handleAcceptPrivacy = () => {
    setForm((p) => ({ ...p, consent: true }));
    setPrivacyModalOpen(false);
    const payload = {
      name: (form.name || '').trim(),
      email: (form.email || '').trim(),
      phone: (form.phone || '').trim(),
      policyVersion: SCREENING_CONSENT_VERSION,
    };
    consentLogPromiseRef.current = hashPolicyText(PRIVACY_POLICY_CONTENT)
      .then((policyTextHash) =>
        axios.post(`/api/screenings/${screeningId}/consent`, { ...payload, policyTextHash }, { timeout: 10000 })
      )
      .then((res) => res.data?.logId || null)
      .catch((err) => {
        console.error('[CONSENT] Failed to record consent log', {
          screeningId,
          httpStatus: err.response?.status,
          serverMessage: err.response?.data?.message,
        });
        return null;
      });
  };

  const buildSteps = () => {
    const steps = ['Basic details'];
    // Interview-only screenings or screenings where resume is already completed skip the Resume upload step entirely
    if (!applyInfo?.isInterviewOnly && !applyInfo?.resumeCompleted) {
      if (applyInfo?.hasResumeScoring !== false) steps.push('Resume upload');
    }
    // Documents are collected after Resume Upload, before Assessment/Interview
    if (applyInfo?.includeDocumentCollection) steps.push('Document upload');
    if (applyInfo?.includeApplicationForm) steps.push('Application form');
    if (applyInfo?.hasAssessment) steps.push('Assessment');
    if (applyInfo?.hasInterview) steps.push('AI interview');
    return steps;
  };

  const getTotalTimeString = () => {
    if (!applyInfo) return t('1 hour');
    let totalMins = 0;
    
    // Resume upload (estimate 1 min)
    if (applyInfo.hasResumeScoring !== false && !applyInfo.isInterviewOnly) {
      totalMins += 1;
    }
    
    if (applyInfo.hasAssessment) {
      totalMins += applyInfo.assessmentDurationMinutes || 30; // default 30 if missing
    }
    
    if (applyInfo.hasInterview) {
      totalMins += applyInfo.interviewDurationMinutes || 30; // default 30 if missing
    }
    
    if (totalMins === 0) return t('1 hour'); // fallback
    
    if (totalMins === 60) return t('1 hour');
    if (totalMins > 60) {
      const h = Math.floor(totalMins / 60);
      const m = totalMins % 60;
      return m === 0 ? (h > 1 ? t('{h} hours', { h }) : t('{h} hour', { h })) : t('{h} hr {m} mins', { h, m });
    }
    return t('{n} minutes', { n: totalMins });
  };

  const getButtonText = () => {
    if (submitting) return t('Submitting\u2026');
    const hasAssessment = !!applyInfo?.hasAssessment;
    const hasInterview = !!applyInfo?.hasInterview;
    const hasResumeScoring = applyInfo?.hasResumeScoring !== false;

    // Resume-only screening: basic details -> resume upload -> thank you
    if (hasResumeScoring && !hasAssessment && !hasInterview) return t('Next');

    // Document upload comes after this form, so the application is not submitted yet.
    if (applyInfo?.includeDocumentCollection && !applyInfo?.documentCollectionCompleted) return t('Next');
    if (applyInfo?.includeApplicationForm && !applyInfo?.applicationFormCompleted) return t('Next');

    return (hasAssessment || hasInterview) ? t('Continue to Screening') : t('Submit Application');
  };

  const executeSubmit = async () => {
    const name = (form.name || '').trim();
    const email = (form.email || '').trim();
    setError('');
    setSubmitting(true);
    try {
      const ageField = applyInfo?.registrationFields?.find(f => f.fieldType === 'age' || f.label?.toLowerCase() === 'age');
      const genderField = applyInfo?.registrationFields?.find(f => f.fieldType === 'gender' || f.label?.toLowerCase() === 'gender');
      const dobField = applyInfo?.registrationFields?.find(f => f.fieldType === 'dob' || f.label?.toLowerCase() === 'dob' || f.label?.toLowerCase() === 'date of birth');
      const ageValue = ageField ? form.customFields[ageField.fieldId] : undefined;
      const genderValue = genderField ? form.customFields[genderField.fieldId] : undefined;
      const dobValue = dobField ? form.customFields[dobField.fieldId] : undefined;

      // The input's own `required` is not enough: the browser skips it on a disabled
      // field and on any submit that is not a native form submit. Screenings that read
      // Aadhaar/PAN depend on this value to tell a typo from a stranger's card.
      if (dobField?.required && !String(dobValue || '').trim()) {
        setError('Please enter your Date of Birth in DD/MM/YYYY format.');
        setSubmitting(false);
        return;
      }

      if (dobValue && !/^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/.test(dobValue)) {
        setError('Please enter a valid Date of Birth in DD/MM/YYYY format.');
        setSubmitting(false);
        return;
      }

      await axios.post(`/api/screenings/${screeningId}/basic-apply`, { 
        name, 
        email, 
        phone: (form.phone || '').trim(),
        age: ageValue,
        dob: dobValue,
        gender: genderValue,
        storeId: form.storeId || undefined,
        cityId: form.cityId || undefined,
      });
      let startRes;
      try {
        startRes = await axios.post(`/api/screenings/${screeningId}/start`, {
          name,
          email,
          phone: (form.phone || '').trim(),
          consentGiven: true,
          consentVersion: SCREENING_CONSENT_VERSION,
          consentLogId: (await consentLogPromiseRef.current) || undefined,
          noticeConsentLogId: noticeConsentLogId || undefined,
          storeId: form.storeId || undefined,
          cityId: form.cityId || undefined,
        });
      }
      catch (timerErr) {
        // /start creates the Candidate record, so everything downstream depends
        // on it. Log it explicitly: a silent failure here shows up later as a
        // confusing 404 from custom-fields and a resume rejection from /apply.
        console.error('[APPLY_FLOW] /start failed — candidate record was not created', {
          screeningId,
          email,
          httpStatus: timerErr.response?.status,
          serverMessage: timerErr.response?.data?.message,
        });
        if (timerErr.response?.data?.code === 'NOTICE_CONSENT_REQUIRED' && consentNotice) {
          // Stored agreement is not usable (e.g. a new notice version): ask again.
          saveNoticeConsent(screeningId, consentNotice, '');
          setNoticeConsentLogId('');
          setNoticeOpen(true);
        }
        setError(timerErr.response?.data?.message || 'Unable to start your screening timer. Please try again.');
        return;
      }
      
      // Save custom fields to Candidate database immediately via the new API
      if (Object.keys(form.customFields).length > 0 || ageValue || genderValue) {
        try {
          await axios.post(`/api/screenings/${screeningId}/custom-fields`, {
            email,
            customFields: form.customFields,
            age: ageValue,
            dob: dobValue,
            gender: genderValue
          });
        } catch (customFieldsErr) {
          console.error('Failed to save custom fields to candidate DB:', customFieldsErr);
        }
      }
      const hasAssessment = !!applyInfo?.hasAssessment;
      const hasInterview = !!applyInfo?.hasInterview;

      const basicPayload = {
        name,
        email,
        phone: (form.phone || '').trim(),
        age: ageValue,
        dob: dobValue,
        gender: genderValue,
        hasAssessment,
        hasInterview,
        customFields: form.customFields,
        storeId: form.storeId || undefined,
        cityId: form.cityId || undefined,
      };
      saveResumeStageAfterBasic(screeningId, basicPayload);

      // If resume scoring is disabled, there's no need to show the "Additional details" page (since we removed age, CTC, NP).
      // Submit /apply with empty extra details and navigate directly to the next step.
      const isResumeAlreadyDone = !!startRes?.data?.resumeCompleted;
      // Only take the no-resume shortcut when applyInfo has actually loaded and
      // positively says a resume is not needed. Treating an unloaded/failed
      // applyInfo as "skip" posts to /apply with no file, and the server rightly
      // rejects it with "Resume file (PDF, DOC, DOCX) is required" — which the
      // candidate sees on the Basic Details form with no way to recover.
      const skipResumeUpload = !!applyInfo && (
        applyInfo.hasResumeScoring === false ||
        applyInfo.isInterviewOnly === true ||
        isResumeAlreadyDone
      );
      if (skipResumeUpload) {
        try {
          const data = new FormData();
          data.append('name', name);
          data.append('email', email);
          data.append('phone', (form.phone || '').trim());
          data.append('noticePeriod', '');
          data.append('currentCtc', '');
          data.append('expectedCtc', '');
          if (ageValue) data.append('age', ageValue);
          if (dobValue) data.append('dob', dobValue);
          if (genderValue) data.append('gender', genderValue);
          if (form.storeId) data.append('storeId', form.storeId);
          if (form.cityId) data.append('cityId', form.cityId);
          if (Object.keys(form.customFields).length > 0) {
            data.append('customFields', JSON.stringify(form.customFields));
          }
          // Records why this no-file /apply was sent. The server logs it beside
          // the rejection, so if the skip decision is wrong we can see exactly
          // which condition caused it instead of inferring from symptoms.
          data.append(
            'clientFileState',
            JSON.stringify({
              source: 'executeSubmit:skipResumeUpload',
              hasResumeScoring: applyInfo?.hasResumeScoring ?? null,
              isInterviewOnly: applyInfo?.isInterviewOnly ?? null,
              resumeCompleted: isResumeAlreadyDone,
              applyInfoLoaded: !!applyInfo,
              startResOk: !!startRes?.data,
            })
          );
          // No explicit Content-Type: the browser must set it to add the
          // multipart boundary. Hard-coding it yields a boundary-less header
          // that stricter engines (Brave, in-app/proxy browsers) reject before
          // sending, which read as a random network failure on upload.
          const applyRes = await axios.post(`/api/screenings/${screeningId}/apply`, data);
          markHubStage(screeningId, email, name);
          
          const { assessmentSharingLink, interviewSharingLink, documentCollectionToken, applicationFormToken } =
            applyRes.data || {};
          const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    
          const params = new URLSearchParams();
          const hasEmail = email.trim().length > 0;
          const hasName = name.trim().length > 0;
          if (hasEmail) params.set('email', email.trim());
          if (hasName) params.set('name', name.trim());
    
          const hubQuery = params.toString();
          const hubPath = `/apply/${screeningId}/hub${hubQuery ? `?${hubQuery}` : ''}`;
          const hubReturnUrl = baseUrl + hubPath;

          const isAssessmentPending = assessmentSharingLink && !applyInfo?.assessmentCompleted;
          const isInterviewPending = interviewSharingLink && !applyInfo?.interviewCompleted;
          const isDocumentCollectionPending = documentCollectionToken && applyInfo?.includeDocumentCollection;

          if (isDocumentCollectionPending) {
            const docParams = new URLSearchParams(params);
            docParams.set('token', documentCollectionToken);
            navigate(`/apply/${screeningId}/documents?${docParams.toString()}`);
          } else if (applicationFormToken) {
            navigate(applicationFormPath(screeningId, applicationFormToken, { email, name }));
          } else if (isAssessmentPending) {
            let assessmentReturnUrl = hubReturnUrl;
    
            if (isInterviewPending) {
              const interviewQuery = new URLSearchParams();
              interviewQuery.set('returnUrl', hubReturnUrl);
              interviewQuery.set('screeningId', screeningId);
              if (hasEmail) interviewQuery.set('email', email.trim());
              if (hasName) interviewQuery.set('name', name.trim());
              interviewQuery.set('hasAssessment', 'true');
              const interviewUrl = `/ai-interview/${interviewSharingLink}?${interviewQuery.toString()}`;
              assessmentReturnUrl = baseUrl + interviewUrl;
            }
    
            const assessmentQuery = new URLSearchParams();
            assessmentQuery.set('returnUrl', assessmentReturnUrl);
            assessmentQuery.set('screeningId', screeningId);
            if (hasEmail) assessmentQuery.set('email', email.trim());
            if (hasName) assessmentQuery.set('name', name.trim());
            if (interviewSharingLink) {
              assessmentQuery.set('hasInterview', 'true');
              assessmentQuery.set('interviewSharingLink', interviewSharingLink);
            }
            const assessmentUrl = `/assessment/${assessmentSharingLink}?${assessmentQuery.toString()}`;
            navigate(assessmentUrl);
          } else if (isInterviewPending) {
            const interviewQuery = new URLSearchParams();
            interviewQuery.set('returnUrl', hubReturnUrl);
            interviewQuery.set('screeningId', screeningId);
            if (hasEmail) interviewQuery.set('email', email.trim());
            if (hasName) interviewQuery.set('name', name.trim());
            if (assessmentSharingLink) {
              interviewQuery.set('hasAssessment', 'true');
            }
            const interviewUrl = `/ai-interview/${interviewSharingLink}?${interviewQuery.toString()}`;
            navigate(interviewUrl);
          } else {
            navigate(`/apply/${screeningId}/hub${hubQuery ? `?${hubQuery}` : ''}`, { replace: true });
          }
        } catch (applyErr) {
          // If the server says a resume is required, our "skip" decision was
          // wrong — send the candidate to the upload step instead of stranding
          // them on this form with an error they cannot act on.
          const serverMsg = applyErr.response?.data?.message || '';
          if (/resume file/i.test(serverMsg)) {
            navigate(`/apply/${screeningId}/resume`, { state: { basicDetails: basicPayload } });
            return;
          }
          setError(serverMsg || 'Failed to submit your application. Please try again.');
          reportDiag(
            screeningId,
            'details',
            'skip_resume_apply_failed',
            {
              httpStatus: applyErr?.response?.status ?? null,
              errorCode: applyErr?.code || null,
              errorMessage: String(applyErr?.message || '').slice(0, 120),
              isNetworkFailure: !applyErr?.response,
              serverMessage: String(serverMsg).slice(0, 160),
              routedToResume: /resume file/i.test(serverMsg),
            },
            { email: basicPayload?.email, name: basicPayload?.name }
          );
        }
        return;
      }

      // If resume scoring is enabled, explicitly route to /resume to collect the file
      navigate(`/apply/${screeningId}/resume`, {
        state: { basicDetails: basicPayload },
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to continue. Please check your details and try again.');
      // This is step one: a candidate blocked here never appears anywhere else
      // in the logs, so without this they are simply absent rather than failed.
      reportDiag(
        screeningId,
        'details',
        'continue_failed',
        {
          httpStatus: err?.response?.status ?? null,
          errorCode: err?.code || null,
          errorMessage: String(err?.message || '').slice(0, 120),
          isNetworkFailure: !err?.response,
          serverMessage: String(err?.response?.data?.message || '').slice(0, 160),
        },
        { email: form?.email, name: form?.name }
      );
    }
    finally { setSubmitting(false); }
  };

  const handleBasicDetailsSubmit = async (e) => {
    e.preventDefault();
    // Consent stays gated in the browser: it is a legal precondition and the
    // candidate must actively agree before anything is sent. Everything else
    // (name/email, store code, city) is validated and logged by the backend so
    // failures leave a server-side trace we can diagnose.
    if (consentNotice && !noticeConsentLogId) { setError('Please read and agree to the consent notice to continue.'); return; }
    if (!form.consent) { setError('Please accept the consent to continue.'); return; }
    if (applyInfo?.hasAssessment || applyInfo?.hasInterview) { setConfirmModalOpen(true); return; }
    await executeSubmit();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Store code, city and resume file type are all enforced by the backend,
    // which logs each rejection — a browser-side block leaves no server record
    // and made candidate-reported failures impossible to trace.
    // Prefer the input's own file: on Samsung Android 12 the File held in state
    // can go stale, so form.resume is empty while the candidate still sees their
    // filename and the request goes out with no resume attached.
    const resumeToSend = resumeInputRef.current?.files?.[0] || form.resume || null;
    if (!resumeToSend) return;
    setSubmitting(true); setError('');
    try {
      const ageField = applyInfo?.registrationFields?.find(f => f.fieldType === 'age' || f.label?.toLowerCase() === 'age');
      const dobField = applyInfo?.registrationFields?.find(f => f.fieldType === 'dob' || f.label?.toLowerCase() === 'dob' || f.label?.toLowerCase() === 'date of birth');
      const genderField = applyInfo?.registrationFields?.find(f => f.fieldType === 'gender' || f.label?.toLowerCase() === 'gender');
      
      const dobValue = dobField ? form.customFields[dobField.fieldId] : undefined;
      if (dobValue && !/^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/.test(dobValue)) {
        setError('Please enter a valid Date of Birth in DD/MM/YYYY format.');
        setSubmitting(false);
        return;
      }

      const data = new FormData();
      data.append('name', (form.name || '').trim());
      data.append('email', form.email.trim());
      data.append('phone', (form.phone || '').trim());
      data.append('noticePeriod', ''); data.append('currentCtc', ''); data.append('expectedCtc', '');
      if (ageField && form.customFields[ageField.fieldId]) data.append('age', form.customFields[ageField.fieldId]);
      if (dobField && form.customFields[dobField.fieldId]) data.append('dob', form.customFields[dobField.fieldId]);
      if (genderField && form.customFields[genderField.fieldId]) data.append('gender', form.customFields[genderField.fieldId]);
      if (form.storeId) data.append('storeId', form.storeId);
      if (form.cityId) data.append('cityId', form.cityId);
      data.append('resume', resumeToSend, resumeToSend.name);
      if (Object.keys(form.customFields).length > 0) {
        data.append('customFields', JSON.stringify(form.customFields));
      }
      // No explicit Content-Type — the browser adds the multipart boundary.
      const res = await axios.post(`/api/screenings/${screeningId}/apply`, data);
      setThankYouData(res.data);
      const { assessmentSharingLink, interviewSharingLink, documentCollectionToken, applicationFormToken } =
        res.data || {};
      const params = new URLSearchParams();
      if (form.email?.trim()) params.set('email', form.email.trim());
      if (form.name?.trim()) params.set('name', form.name.trim());
      const query = params.toString();

      if (documentCollectionToken && applyInfo?.includeDocumentCollection) {
        params.set('token', documentCollectionToken);
        navigate(`/apply/${screeningId}/documents?${params.toString()}`, { replace: true });
      } else if (applicationFormToken) {
        navigate(applicationFormPath(screeningId, applicationFormToken, { email: form.email, name: form.name }), {
          replace: true,
        });
      } else if (assessmentSharingLink || interviewSharingLink) {
        navigate(`/apply/${screeningId}/hub${query ? `?${query}` : ''}`, { replace: true });
      } else { setStep('thanks'); }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit application. Please try again.');
      reportDiag(
        screeningId,
        'details',
        'apply_submit_failed',
        {
          fileName: String(resumeToSend?.name || '').slice(0, 80),
          fileSizeKB: Math.round((resumeToSend?.size || 0) / 1024),
          fileType: resumeToSend?.type || 'none',
          httpStatus: err?.response?.status ?? null,
          errorCode: err?.code || null,
          errorMessage: String(err?.message || '').slice(0, 120),
          isNetworkFailure: !err?.response,
          isTimeout: err?.code === 'ECONNABORTED',
          wentOffline: typeof navigator !== 'undefined' ? navigator.onLine === false : null,
          serverMessage: String(err?.response?.data?.message || '').slice(0, 160),
        },
        { email: form?.email, name: form?.name }
      );
    }
    finally { setSubmitting(false); }
  };

  useEffect(() => { if (privacyModalOpen) { setCanAcceptPrivacy(true); } }, [privacyModalOpen]);

  const steps = buildSteps();
  const interviewType = applyInfo?.interviewType || 'voice';
  const STEP_META = getStepMeta(interviewType);
  const jobTitle = applyInfo?.jobTitle || t('Position');
  const companyName = applyInfo?.companyName || '';

  // Default fields (name/email/phone) keep their fixed bindings + icons, but their
  // label / placeholder / required state are admin-configurable via registrationFields.
  // Fall back to the original hardcoded copy when no config is present (backward compatible).
  const getRegField = (id) =>
    (Array.isArray(applyInfo?.registrationFields)
      ? applyInfo.registrationFields.find((f) => f.fieldId === id)
      : null) || null;
  const nameField = getRegField('name');
  const emailField = getRegField('email');
  const phoneField = getRegField('phone');
  const phoneRequired = phoneField ? !!phoneField.required : false;

  // Invite links lock the details the recruiter already holds. The DOB is never one of
  // them: apply-info does not return it (it is public, keyed only by email, and a DOB is
  // not for anyone who knows an address), so a locked DOB field was always an empty one —
  // and a disabled required field lets the form submit without it. Screenings that read
  // Aadhaar/PAN rely on that value to tell a typo from a stranger's card.
  const isCustomFieldLocked = (field) => !!emailFromQuery && field.fieldType !== 'dob';

  // Build confirmation modal step list
  const confirmSteps = [];
  if (applyInfo?.hasResumeScoring !== false && !applyInfo?.resumeCompleted) {
    confirmSteps.push({ label: 'Resume Upload', desc: 'Upload your resume', icon: DocumentTextIcon });
  }
  if (applyInfo?.hasAssessment) confirmSteps.push({ label: 'AI Assessment', desc: 'Complete the assessment', icon: AcademicCapIcon });
  if (applyInfo?.hasInterview) {
    const isVideo = interviewType === 'video';
    confirmSteps.push({
      label: isVideo ? 'AI Video Interview' : 'AI Voice Interview',
      desc: isVideo ? 'AI video interview' : 'AI voice interview',
      icon: isVideo ? VideoCameraIcon : MicrophoneIcon,
    });
  }

  /* ─── THANK YOU SCREEN ─── */
  if (step === 'thanks') {
    const hasAssessment = thankYouData?.assessmentSharingLink;
    const hasInterview = thankYouData?.interviewSharingLink;
    return (
      <div className="min-h-screen bg-white-smoke flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-4xl rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-lavender-purple-50">
            <span className="text-lavender-purple text-xl">{'\u2713'}</span>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 text-center">{t('Thank you for applying')}</h1>
          <p className="mt-2 text-sm text-gray-600 text-center">{t('Your application has been received.')}</p>
          {!hasAssessment && !hasInterview && (
            <p className="mt-4 text-xs text-gray-500 text-center">{t('No further steps needed. The recruiter will review your profile.')}</p>
          )}
          {(hasAssessment || hasInterview) && (
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {hasAssessment && (
                <button type="button" onClick={() => navigate(`/assessment/${thankYouData.assessmentSharingLink}`, { state: { fromScreening: true, candidateInfo: thankYouData.candidateInfo, screeningId } })}
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-left text-sm font-medium text-gray-900 hover:border-electric-blue hover:bg-electric-blue-50 transition shadow-sm">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('Step A')}</div>
                  <div className="mt-1 text-sm font-semibold text-gray-900">{t('Take assessment')}</div>
                </button>
              )}
              {hasInterview && (
                <button type="button" onClick={() => navigate(`/ai-interview/${thankYouData.interviewSharingLink}`, { state: { fromScreening: true, candidateInfo: thankYouData.candidateInfo, hasAssessment: !!thankYouData?.assessmentSharingLink, screeningId } })}
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-left text-sm font-medium text-gray-900 hover:border-electric-blue hover:bg-electric-blue-50 transition shadow-sm">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('Step B')}</div>
                  <div className="mt-1 text-sm font-semibold text-gray-900">
                    {interviewType === 'video' ? t('Take AI video interview') : t('Take AI voice interview')}
                  </div>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ─── LINK INACTIVE ─── */
  if (linkInactive && !applyInfoLoading) {
    return (
      <div className="min-h-screen bg-white-smoke flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-sm text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50">
            <LinkIcon className="h-6 w-6 text-amber-600" aria-hidden />
          </div>
          <h1 className="text-xl font-semibold text-gray-900">{t('This application link is no longer active')}</h1>
          <p className="mt-2 text-sm text-gray-600">{t('The recruiter has disabled this link.')}</p>
        </div>
      </div>
    );
  }



  const isBasicDetailsView = journeyStage === 'basic';

  /* ═══════════════════════════════════════════════
     MAIN APPLY PAGE
     ═══════════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-white-smoke px-4 py-4 sm:py-6 md:py-8">
      <LeaveScreeningModal
        isOpen={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        onStay={() => setShowLeaveModal(false)}
        onLeave={() => { setShowLeaveModal(false); navigate(-1); }}
        title="Leave this page?"
        message="Refreshing or closing this page may auto-submit your assessment and affect your progress. Please stay on this page until you complete this step or your time runs out."
      />
      {/* ── Hiring org's own consent notice (e.g. ajanta_consent_notice); opened from its checkbox in the form ── */}
      {consentNotice && (
        <ConsentNoticeModal
          isOpen={noticeOpen}
          notice={consentNotice}
          mode={noticeConsentLogId ? 'review' : 'consent'}
          submitting={noticeSubmitting}
          errorMessage={noticeError}
          onAgree={handleNoticeAgree}
          onClose={() => setNoticeOpen(false)}
        />
      )}

      {/* ── Privacy Policy Modal ── */}
      <AnimatedModal isOpen={privacyModalOpen} onClose={() => { setPrivacyModalOpen(false); setCanAcceptPrivacy(false); }}
        contentClassName="max-w-2xl w-full rounded-2xl border border-gray-200 bg-white shadow-xl overflow-hidden" backdropClassName="bg-gray-900/50" ariaLabelledBy="privacy-policy-title">
        <div className="p-6 sm:p-8">
          <h2 id="privacy-policy-title" className="text-lg font-bold text-gray-900 sm:text-xl">{t('Privacy Policy')}</h2>
          <div className="mt-4 max-h-[50vh] overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 leading-relaxed" tabIndex={0}>
            <div className="whitespace-pre-wrap font-sans text-left">
              {PRIVACY_POLICY_CONTENT.split('\n').map((line, i) => {
                const isClauseTitle = /^\d+(\.\d+)?\.\s+.+$/.test(line.trim());
                if (line.trim() === 'Company Name: TalentCrux') return <span key={i} className="block">Company Name: <span className="font-semibold text-gray-900">TalentCrux</span></span>;
                return isClauseTitle
                  ? <span key={i} className="block font-semibold text-gray-900">{line}</span>
                  : <span key={i} className="block whitespace-pre-wrap">{line || '\u00A0'}</span>;
              })}
            </div>
          </div>
          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end sm:gap-3">
            <button type="button" onClick={() => setPrivacyModalOpen(false)} className="w-full sm:w-auto rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50">{t('Decline')}</button>
            <button type="button" onClick={handleAcceptPrivacy} disabled={!canAcceptPrivacy}
              data-testid="accept-privacy-btn"
              className="w-full sm:w-auto rounded-lg bg-electric-blue px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-electric-blue-600 disabled:opacity-50 disabled:cursor-not-allowed">{t('I Accept')}</button>
          </div>
        </div>
      </AnimatedModal>

      {/* ── Confirmation Modal ── */}
      <AnimatedModal isOpen={confirmModalOpen} onClose={() => setConfirmModalOpen(false)}
        contentClassName="max-w-2xl w-full rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden" backdropClassName="bg-gray-900/60" ariaLabelledBy="confirm-start-title">
        <div className="p-0">
          <div className="bg-electric-blue px-6 py-5 sm:px-8 sm:py-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm"><ShieldCheckIcon className="h-5 w-5 text-white" /></div>
              <div>
                <h2 id="confirm-start-title" className="text-lg font-bold text-white sm:text-xl">{t('Ready to begin?')}</h2>
                <p className="text-sm text-blue-100 mt-0.5">{t('Please start only if you meet the requirements below')}</p>
              </div>
            </div>
          </div>
          <div className="px-6 py-5 sm:px-8 sm:py-6">
            <div className="rounded-xl border-2 border-amber-200 bg-amber-50/50 p-4">
              <div className="flex items-center gap-2 mb-3">
                <ExclamationTriangleIcon className="text-amber-600" style={{ width: 18, height: 18 }} />
                <p className="text-sm font-bold text-amber-900">{t('Important guidelines')}</p>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                <li className="flex items-center gap-2.5">
                  <ClockIcon className="h-4 w-4 text-amber-600 flex-shrink-0" />
                  <span className="text-[13px] font-medium text-amber-800">{t('Once started, you will have only {time} to complete this.', { time: getTotalTimeString() })}</span>
                </li>
                {((applyInfo?.hasResumeScoring !== false && !applyInfo?.isInterviewOnly) || applyInfo?.includeDocumentCollection) && (
                  <li className="flex flex-col gap-1.5 w-full">
                    <div className="flex items-center gap-2.5">
                      <DocumentTextIcon className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <span className="text-[13px] font-medium text-amber-800">{t('Keep the below documents ready:')}</span>
                    </div>
                    <ul className="ml-7 list-disc text-[12px] font-medium text-amber-700/80 space-y-0.5 pr-2">
                      {applyInfo?.hasResumeScoring !== false && !applyInfo?.isInterviewOnly && (
                        <li><strong>{t('Resume / CV')}</strong></li>
                      )}
                      {applyInfo?.includeDocumentCollection && applyInfo?.requestedDocuments?.length > 0 ? (
                        applyInfo.requestedDocuments.map((doc, idx) => (
                          <li key={idx}><strong>{typeof doc === 'string' ? doc : doc.label || doc.title || doc.name || t('Required Document')}</strong></li>
                        ))
                      ) : applyInfo?.includeDocumentCollection ? (
                        <li><strong>{t('Identity & Verification Documents (PAN, Aadhaar, etc.)')}</strong></li>
                      ) : null}
                    </ul>
                  </li>
                )}
                {applyInfo?.hasAssessment && (
                  <>
                    <li className="flex items-center gap-2.5">
                      <VideoCameraIcon className="h-4 w-4 text-amber-600 flex-shrink-0" />
                      <span className="text-[13px] font-medium text-amber-800">
                        {applyInfo?.assessmentProctoringConfig?.video ? t('Video will be recorded (Proctored)') : t('Camera might be turned on')}
                      </span>
                    </li>
                    <li className="flex items-center gap-2.5">
                      <SparklesIcon className="h-4 w-4 text-amber-600 flex-shrink-0" />
                      <span className="text-[13px] font-medium text-amber-800">{t('Ensure a quiet, distraction-free space')}</span>
                    </li>
                    {applyInfo?.assessmentProctoringConfig?.video && (
                      <li className="flex items-center gap-2.5">
                        <ShieldCheckIcon className="h-4 w-4 text-amber-600 flex-shrink-0" />
                        <span className="text-[13px] font-medium text-amber-800">{t('Do not cheat or switch tabs')}</span>
                      </li>
                    )}
                  </>
                )}
                {applyInfo?.hasInterview && (
                  <li className="flex items-center gap-2.5">
                    <MicrophoneIcon className="h-4 w-4 text-amber-600 flex-shrink-0" />
                    <span className="text-[13px] font-medium text-amber-800">
                      {applyInfo?.interviewType === 'video'
                        ? t('Find a quiet space. No cheating (your video and audio are recorded).')
                        : t('Find a quiet space. No cheating (your audio is recorded).')}
                    </span>
                  </li>
                )}
                {(applyInfo?.interviewType === 'video' || applyInfo?.assessmentProctoringConfig?.video) ? (
                  <li className="flex items-center gap-2.5">
                    <UserIcon className="h-4 w-4 text-amber-600 flex-shrink-0" />
                    <span className="text-[13px] font-medium text-amber-800">{t('Dress appropriately and stay seated.')}</span>
                  </li>
                ) : (
                  <li className="flex items-center gap-2.5">
                    <UserIcon className="h-4 w-4 text-amber-600 flex-shrink-0" />
                    <span className="text-[13px] font-medium text-amber-800">{t('Dress appropriately.')}</span>
                  </li>
                )}
              </ul>
            </div>

            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end sm:gap-3">
              <button type="button" onClick={() => setConfirmModalOpen(false)}
                className="w-full sm:w-auto rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-600 shadow-sm hover:bg-gray-50 transition-colors">{t('Not now')}</button>
              <button type="button" onClick={() => { setConfirmModalOpen(false); setShowDeviceCheckModal(true); }} disabled={submitting}
                data-testid="ready-to-begin-btn"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-electric-blue to-indigo-600 px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-electric-blue/20 hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:hover:translate-y-0">
                {submitting ? t('Starting\u2026') : <>{t("I'm ready, let's go")} <ArrowRightIcon className="h-4 w-4" /></>}
              </button>
            </div>
          </div>
        </div>
      </AnimatedModal>

      <DeviceCheckModal
        isOpen={showDeviceCheckModal}
        onClose={() => setShowDeviceCheckModal(false)}
        interviewType={applyInfo?.interviewType === 'video' || applyInfo?.assessmentProctoringConfig?.cameraMonitoring?.enabled || applyInfo?.assessmentProctoringConfig?.cameraMonitoring?.requireCamera ? 'video' : 'voice'}
        onProceed={async () => {
          markDeviceAsChecked();
          setShowDeviceCheckModal(false);
          await executeSubmit();
        }}
      />

      {/* E2E-only bypass: the real device check requires actual detected microphone
          energy (DeviceCheckModal.js), which a headless browser's fake media device
          cannot produce, so this exists purely to let automated tests get past it.
          Hard-gated to never render on the real production host — see isProdHost.js. */}
      {!isProdHost() && (
        <button
          type="button"
          data-testid="e2e-force-submit-btn"
          onClick={executeSubmit}
          className="absolute opacity-0 w-0 h-0 overflow-hidden"
          aria-hidden="true"
        >
          Force Submit
        </button>
      )}

      {/* ── Job Description Modal ── */}
      <AnimatedModal isOpen={jobDescriptionModalOpen} onClose={() => setJobDescriptionModalOpen(false)}
        contentClassName="max-w-2xl w-full rounded-2xl border border-gray-200 bg-white shadow-xl overflow-hidden" backdropClassName="bg-gray-900/50" ariaLabelledBy="jd-modal-title">
        <div className="p-6 sm:p-8">
          <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-4">
            <div>
              <h2 id="jd-modal-title" className="text-lg font-bold text-gray-900 sm:text-xl">{jobTitle}</h2>
              {companyName && companyName !== 'the recruiter' && (
                <p className="text-sm text-gray-500 mt-1">{companyName}</p>
              )}
            </div>
          </div>
          
          <div className="max-h-[60vh] overflow-y-auto pr-2">


            {applyInfo?.jobDescription && (
              <div className="mb-6">
                <h3 className="text-sm font-bold text-gray-900 mb-2">{t('Job Description')}</h3>
                <div className="text-sm text-gray-700 leading-relaxed space-y-3">
                  {applyInfo.jobDescription.split('\n').map((paragraph, i) => (
                    <p key={i}>{paragraph}</p>
                  ))}
                </div>
              </div>
            )}


          </div>
          
          <div className="mt-6 flex justify-end border-t border-gray-100 pt-4">
            <button type="button" onClick={() => setJobDescriptionModalOpen(false)} 
              className="rounded-lg bg-electric-blue px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-electric-blue-600">
              {t('Close')}
            </button>
          </div>
        </div>
      </AnimatedModal>

      <div className="mx-auto max-w-5xl">
        {/* ── Header ── */}
        <header className="flex items-center justify-between gap-3">
          <div className="flex flex-col min-w-0">
            <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide text-gray-400">{t('Powered by')}</p>
            <div className="mt-0.5 flex items-center gap-1.5">
              <img src="/logo.svg" alt="TalentCrux" className="h-9 w-9 sm:h-12 sm:w-12 md:h-14 md:w-14 object-contain flex-shrink-0" />
              <span className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 truncate">TalentCrux</span>
            </div>
          </div>
        </header>

        {/* ═══ HERO: Timeline + Congrats + JD Strip ═══ */}
        {isBasicDetailsView && applyInfo && (
          <div className="mt-6 sm:mt-8">
            {/* Process Timeline */}
            <div className="mt-8 sm:mt-10">
              <p className="text-center text-[11px] sm:text-sm font-semibold uppercase tracking-[0.22em] text-gray-500 mb-5 sm:mb-7">
                {t('Your screening journey')}
              </p>
              <div className="flex items-start justify-center gap-0">
                {steps.map((label, index) => {
                  const meta = STEP_META[label] || { icon: CheckIcon, desc: '', gradient: 'from-gray-400 to-gray-500' };
                  const StepIcon = meta.icon;
                  const isActive = index === 0;
                  return (
                    <React.Fragment key={label}>
                      <div className="flex flex-col items-center text-center min-w-0 flex-1 sm:flex-none sm:w-32">
                        <span className={`text-[9px] sm:text-xs font-semibold uppercase tracking-[0.06em] sm:tracking-[0.16em] whitespace-nowrap mb-1.5 ${isActive ? 'text-electric-blue' : 'text-gray-300'}`}>{t('Step {n}', { n: index + 1 })}</span>
                        <div className={`relative flex h-12 w-12 sm:h-16 sm:w-16 items-center justify-center rounded-2xl transition-all duration-500 ${isActive ? `bg-gradient-to-br ${meta.gradient} text-white shadow-lg` : 'bg-white text-gray-300 border-2 border-gray-200 border-dashed'}`}
                          style={isActive ? { boxShadow: '0 8px 25px rgba(0, 123, 255, 0.2)' } : {}}>
                          <StepIcon className="h-5 w-5 sm:h-7 sm:w-7" strokeWidth={isActive ? 2 : 1.5} />
                          {isActive && <span className="absolute inset-0 rounded-2xl border-2 border-electric-blue/30 animate-ping" style={{ animationDuration: '2s' }} />}
                        </div>
                        <p className={`mt-2 text-[10px] sm:text-sm font-semibold leading-tight hyphens-auto [overflow-wrap:anywhere] ${isActive ? 'text-gray-900' : 'text-gray-400'}`}>
                          {t(label === 'AI interview' ? getInterviewStepLabel(interviewType) : label)}
                        </p>
                      </div>
                      {index < steps.length - 1 && (
                        <div className="flex-1 flex items-center pt-[52px] sm:pt-[52px] min-w-[6px] max-w-[12px] sm:max-w-[72px]">
                          <div className="w-full flex items-center gap-0.5">
                            {[...Array(4)].map((_, i) => (
                              <div
                                // eslint-disable-next-line react/no-array-index-key
                                key={i}
                                className={`flex-1 h-0.5 rounded-full ${
                                  index === 0 ? 'bg-electric-blue/40' : 'bg-gray-200'
                                }`}
                              />
                            ))}
            </div>

            {/* Congrats banner + guidance (below steps) */}
           
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* Single congrats card (aligned with below cards) */}
            <div className="mt-8 sm:mt-9 w-full">
              <div className="w-full rounded-2xl bg-electric-blue-50/70 border border-electric-blue-100 px-4 py-3 sm:px-6 sm:py-4 shadow-sm">
                <p className="text-xs sm:text-sm font-semibold text-gray-900">
                  {companyName
                    ? withNodes(t("You've been shortlisted for {job} at {company}."), {
                        job: <span className="text-electric-blue font-bold">{jobTitle}</span>,
                        company: <span className="text-electric-blue font-bold">{companyName}</span>,
                      })
                    : withNodes(t("You've been shortlisted for {job}."), {
                        job: <span className="text-electric-blue font-bold">{jobTitle}</span>,
                      })}
                </p>
                <ul className="mt-2 space-y-1.5 text-xs sm:text-sm text-gray-800">
                  <li className="flex gap-2">
                    <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-electric-blue" />
                    <span>
                      {withNodes(
                        t('You will have approximately {time} to complete all screening steps once you press {button}. Choose a quiet, distraction‑free environment and ensure you have a stable internet connection.'),
                        {
                          time: <span className="font-semibold">{getTotalTimeString()}</span>,
                          button: <span className="font-semibold">{t('Continue to Screening')}</span>,
                        }
                      )}
                    </span>
                  </li>
                </ul>
              </div>
            </div>

          </div>
        )}

        {/* ═══ TWO CARDS: Job overview (left) + Basic Details (right) ═══ */}
        <div
          className={`mt-5 sm:mt-6 gap-4 sm:gap-6 flex flex-col ${
            isBasicDetailsView ? 'lg:grid lg:grid-cols-2' : ''
          } items-stretch`}
        >
          {isBasicDetailsView ? (
            <>
              {/* ── LEFT: Job overview & preparation guide ── */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-6 lg:p-7 order-2 lg:order-1 w-full flex flex-col gap-6">
                {/* Job title + View JD button */}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-base sm:text-lg font-bold text-gray-900 leading-tight">{jobTitle}</h2>
                    {companyName && companyName !== 'the recruiter' && (
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{companyName}</p>
                    )}
                  </div>
                  <button 
                    type="button"
                    onClick={() => setJobDescriptionModalOpen(true)}
                    className="inline-flex flex-shrink-0 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-semibold text-gray-700 hover:border-electric-blue hover:text-electric-blue hover:bg-electric-blue-50 transition-all duration-200 shadow-sm group"
                  >
                    <DocumentTextIcon className="w-4 h-4 text-gray-400 group-hover:text-electric-blue transition-colors" />
                    {t('View JD')}
                  </button>
                </div>

                {/* About Company (Dynamic based on org config) */}
                {applyInfo?.companyDescription && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2 mb-3">
                      <BuildingOfficeIcon className="w-5 h-5 text-electric-blue flex-shrink-0" />
                      <h3 className="text-sm font-bold text-gray-900">
                        {companyName && companyName !== 'the recruiter' ? t('About {company}', { company: companyName }) : t('About the company')}
                      </h3>
                    </div>
                    <ul className="text-sm text-gray-700 leading-relaxed space-y-3 mt-4 p-5 rounded-xl border border-gray-100 bg-white shadow-sm">
                      {applyInfo.companyDescription.split('\n').filter(p => p.trim()).map((paragraph, i) => {
                        const cleanText = paragraph.replace(/^[-*•]\s*/, '').trim();
                        return (
                          <li key={i} className="flex items-start gap-3">
                            <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-electric-blue" />
                            <span>{cleanText}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {/* What's included (Dynamic based on screening config) */}
                <div className="mt-2">
                  <div className="flex items-center gap-2 mb-4">
                    <InformationCircleIcon className="w-5 h-5 text-electric-blue flex-shrink-0" />
                    <h3 className="text-sm font-bold text-gray-900">{t('What to Expect')}</h3>
                  </div>
                  <div className="space-y-3">
                    {applyInfo?.hasResumeScoring !== false && !applyInfo?.isInterviewOnly && (
                      <div className="flex gap-4 items-start p-4 rounded-xl border border-gray-100 bg-white shadow-sm hover:border-blue-200 hover:shadow-md transition-all duration-300 group">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-50 group-hover:bg-blue-100 transition-colors">
                          <DocumentTextIcon className="h-5 w-5 text-electric-blue" />
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-gray-900">{t('Resume Upload')}</h4>
                          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{t('Please keep a soft copy of your resume (PDF or Word) ready to upload.')}</p>
                        </div>
                      </div>
                    )}

                    {applyInfo?.includeDocumentCollection && (
                      <div className="flex gap-4 items-start p-4 rounded-xl border border-gray-100 bg-white shadow-sm hover:border-blue-200 hover:shadow-md transition-all duration-300 group">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-50 group-hover:bg-blue-100 transition-colors">
                          <DocumentTextIcon className="h-5 w-5 text-electric-blue" />
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-gray-900">{t('Document Upload')}</h4>
                          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{t('Please keep digital copies of your background verification documents (like Aadhaar or PAN card) ready to upload.')}</p>
                        </div>
                      </div>
                    )}

                    {applyInfo?.hasAssessment && (
                      <div className="flex gap-4 items-start p-4 rounded-xl border border-gray-100 bg-white shadow-sm hover:border-blue-200 hover:shadow-md transition-all duration-300 group">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-50 group-hover:bg-blue-100 transition-colors">
                          <VideoCameraIcon className="h-5 w-5 text-electric-blue" />
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-gray-900">{t('Online Assessment')}</h4>
                          {applyInfo?.assessmentProctoringConfig?.video ? (
                            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{t('Your video and audio will be recorded for proctoring. Ensure a quiet environment and do not switch tabs.')}</p>
                          ) : (
                            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{t('Please allow camera access and choose a quiet place with minimal distractions before you begin.')}</p>
                          )}
                        </div>
                      </div>
                    )}

                    {applyInfo?.hasInterview && (
                      <div className="flex gap-4 items-start p-4 rounded-xl border border-gray-100 bg-white shadow-sm hover:border-blue-200 hover:shadow-md transition-all duration-300 group">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-50 group-hover:bg-blue-100 transition-colors">
                          <MicrophoneIcon className="h-5 w-5 text-electric-blue" />
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-gray-900">{t('AI Interview')}</h4>
                          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{withNodes(t('An AI will ask you questions. It will be a simple conversation to understand your skills. ensure your {video} is on and you are presentable/in a formal setting'), { video: <b>{t('video')}</b> })}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── RIGHT: Basic Details Form ── */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-6 lg:p-8 order-1 lg:order-2 w-full hover:shadow-md transition-shadow duration-300">
                <h2 className="text-lg font-bold text-gray-900 sm:text-xl">{t('Basic Details')}</h2>
                <p className="mt-0.5 sm:mt-1 text-sm text-gray-500">{t('Enter your details to begin.')}</p>
                <form onSubmit={handleBasicDetailsSubmit} className="mt-4 space-y-3 sm:mt-6 sm:space-y-4" aria-describedby={error ? 'basic-details-error' : undefined}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t(nameField?.label || 'Full name')}<span className="text-red-500">*</span></label>
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400"><UserIcon className="h-5 w-5" /></div>
                      <input type="text" name="name" required value={form.name} onChange={handleChange}
                        disabled={!!emailFromQuery}
                        data-testid="candidate-name-input"
                        className={`block w-full rounded-xl border border-gray-200 py-2.5 pl-10 pr-3 text-sm placeholder-gray-400 focus:border-electric-blue focus:bg-white focus:outline-none focus:ring-2 focus:ring-electric-blue/20 sm:py-3 transition-all ${emailFromQuery ? 'bg-gray-100/70 text-gray-500 cursor-not-allowed' : 'bg-gray-50/50 text-gray-900'}`}
                        placeholder={t(nameField?.placeholder || 'Your full name')} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t(emailField?.label || 'Email')}<span className="text-red-500">*</span></label>
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400"><EnvelopeIcon className="h-5 w-5" /></div>
                      <input type="email" name="email" required value={form.email} onChange={handleChange}
                        disabled={!!emailFromQuery}
                        data-testid="candidate-email-input"
                        className={`block w-full rounded-xl border border-gray-200 py-2.5 pl-10 pr-3 text-sm placeholder-gray-400 focus:border-electric-blue focus:bg-white focus:outline-none focus:ring-2 focus:ring-electric-blue/20 sm:py-3 transition-all ${emailFromQuery ? 'bg-gray-100/70 text-gray-500 cursor-not-allowed' : 'bg-gray-50/50 text-gray-900'}`}
                        placeholder={t(emailField?.placeholder || 'you@example.com')} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t(phoneField?.label || 'Phone number')} {phoneRequired ? <span className="text-red-500">*</span> : <span className="text-gray-400 font-normal">{t('(optional)')}</span>}</label>
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400"><PhoneIcon className="h-5 w-5" /></div>
                      <input type="tel" name="phone" maxLength={10} required={phoneRequired} value={form.phone} onChange={handleChange}
                        disabled={!!emailFromQuery}
                        data-testid="candidate-phone-input"
                        className={`block w-full rounded-xl border border-gray-200 py-2.5 pl-10 pr-3 text-sm placeholder-gray-400 focus:border-electric-blue focus:bg-white focus:outline-none focus:ring-2 focus:ring-electric-blue/20 sm:py-3 transition-all ${emailFromQuery ? 'bg-gray-100/70 text-gray-500 cursor-not-allowed' : 'bg-gray-50/50 text-gray-900'}`}
                        placeholder={t(phoneField?.placeholder || 'e.g. 98765 43210')} />
                    </div>
                  </div>

                  {/* Custom (non-default) registration fields configured by the org admin.
                      name / email / phone are the default fields rendered above and must NOT be
                      duplicated here — they bind to form.name/email/phone, not form.customFields. */}
                  {applyInfo?.registrationFields
                    ?.filter(
                      (field) =>
                        !field.isDefault &&
                        !['name', 'email', 'phone'].includes(field.fieldId)
                    )
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map((field) => (
                      <div key={field.fieldId}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t(field.label)}{field.required && <span className="text-red-500">*</span>}</label>
                        <div className="relative">
                          {field.fieldType === 'dropdown' || field.fieldType === 'gender' ? (
                            <Listbox
                              value={form.customFields[field.fieldId] || ''}
                              onChange={(val) => {
                                handleChange({
                                  target: { name: `custom_${field.fieldId}`, value: val, type: 'text' }
                                });
                              }}
                              disabled={!!emailFromQuery}
                            >
                              {({ open }) => (
                                <div className="relative">
                                  <Listbox.Button
                                    className={`relative w-full cursor-pointer text-left rounded-xl border border-gray-200 py-2.5 pl-3 pr-10 text-sm focus:border-electric-blue focus:bg-white focus:outline-none focus:ring-2 focus:ring-electric-blue/20 sm:py-3 transition-all ${
                                      emailFromQuery ? 'bg-gray-100/70 text-gray-500 cursor-not-allowed' : 'bg-gray-50/50 text-gray-900 hover:bg-gray-50'
                                    }`}
                                  >
                                    <span className={`block truncate ${!form.customFields[field.fieldId] ? 'text-gray-400' : ''}`}>
                                      {form.customFields[field.fieldId] || t(field.placeholder || 'Select...')}
                                    </span>
                                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                      <ChevronDownIcon
                                        className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                                        aria-hidden="true"
                                      />
                                    </span>
                                  </Listbox.Button>
                                  {field.required && (
                                    <input
                                      type="text"
                                      tabIndex={-1}
                                      className="absolute bottom-0 left-1/2 -translate-x-1/2 opacity-0 pointer-events-none w-full h-full"
                                      required
                                      value={form.customFields[field.fieldId] || ''}
                                      onChange={() => {}}
                                    />
                                  )}
                                  <Transition
                                    as={React.Fragment}
                                    leave="transition ease-in duration-100"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                    afterLeave={() => setCustomFieldSearchQueries(prev => ({ ...prev, [field.fieldId]: '' }))}
                                  >
                                    <Listbox.Options className="absolute z-[100] mt-1 max-h-60 w-full overflow-auto rounded-xl bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none border border-gray-100">
                                      <div className="sticky top-0 z-10 bg-white px-3 py-2 border-b border-gray-100">
                                        <div className="relative">
                                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5">
                                            <svg className="h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                                              <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
                                            </svg>
                                          </div>
                                          <input
                                            type="text"
                                            className="block w-full rounded-md border-0 py-1.5 pl-9 pr-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-electric-blue sm:text-sm sm:leading-6"
                                            placeholder={t('Search {field}...', { field: t(field.label || '').toLowerCase() || t('option') })}
                                            value={customFieldSearchQueries[field.fieldId] || ''}
                                            onChange={(e) => setCustomFieldSearchQueries(prev => ({ ...prev, [field.fieldId]: e.target.value }))}
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={(e) => {
                                              if (e.key === ' ') e.stopPropagation();
                                            }}
                                          />
                                        </div>
                                      </div>
                                      {field.options?.filter(opt => opt.toLowerCase().includes((customFieldSearchQueries[field.fieldId] || '').toLowerCase())).length === 0 ? (
                                        <div className="relative cursor-default select-none py-4 px-4 text-gray-500 text-center">
                                          {t('No options found.')}
                                        </div>
                                      ) : (
                                        field.options?.filter(opt => opt.toLowerCase().includes((customFieldSearchQueries[field.fieldId] || '').toLowerCase())).map((opt, i) => (
                                          <Listbox.Option
                                            key={i}
                                            className={({ active }) =>
                                              `relative cursor-pointer select-none py-2.5 pl-4 pr-9 transition-colors ${
                                                active ? 'bg-electric-blue-50 text-electric-blue' : 'text-gray-900'
                                              }`
                                            }
                                            value={opt}
                                          >
                                            {({ selected, active }) => (
                                              <>
                                                <span className={`block truncate ${selected ? 'font-semibold text-electric-blue' : 'font-normal'}`}>
                                                  {opt}
                                                </span>
                                                {selected ? (
                                                  <span
                                                    className={`absolute inset-y-0 right-0 flex items-center pr-3 ${
                                                      active ? 'text-electric-blue' : 'text-electric-blue'
                                                    }`}
                                                  >
                                                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                  </span>
                                                ) : null}
                                              </>
                                            )}
                                          </Listbox.Option>
                                        ))
                                      )}
                                    </Listbox.Options>
                                  </Transition>
                                </div>
                              )}
                            </Listbox>
                          ) : (
                            <input
                              type={field.fieldType === 'number' || field.fieldType === 'age' ? 'number' : field.fieldType === 'date' ? 'date' : 'text'}
                              name={`custom_${field.fieldId}`}
                              required={field.required}
                              value={form.customFields[field.fieldId] || ''}
                              onChange={handleChange}
                              disabled={isCustomFieldLocked(field)}
                              placeholder={field.fieldType === 'dob' ? t('dd/mm/yyyy') : t(field.placeholder || '')}
                              maxLength={field.fieldType === 'dob' ? 10 : undefined}
                              className={`block w-full rounded-xl border border-gray-200 py-2.5 px-3 text-sm focus:border-electric-blue focus:bg-white focus:outline-none focus:ring-2 focus:ring-electric-blue/20 sm:py-3 transition-all ${isCustomFieldLocked(field) ? 'bg-gray-100/70 text-gray-500 cursor-not-allowed' : 'bg-gray-50/50 text-gray-900'}`}
                            />
                          )}
                        </div>
                      </div>
                    ))}

                  {(isGoColors || applyInfo?.storeListEnabled) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t(storeCodeRule.label)} <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                          <MapPinIcon className="h-5 w-5" />
                        </div>
                        <input
                          type="text"
                          inputMode={storeCodeRule.inputMode}
                          maxLength={storeCodeRule.maxLength}
                          name="storeId"
                          required
                          value={form.storeId}
                          onChange={(e) => {
                            const val = storeCodeRule.sanitize(e.target.value);
                            setForm((p) => ({ ...p, storeId: val }));
                            setError('');
                          }}
                          disabled={!!emailFromQuery}
                          className={`block w-full rounded-xl border border-gray-200 py-2.5 pl-10 pr-3 text-sm placeholder-gray-400 focus:border-electric-blue focus:bg-white focus:outline-none focus:ring-2 focus:ring-electric-blue/20 sm:py-3 transition-all ${
                            emailFromQuery ? 'bg-gray-100/70 text-gray-500 cursor-not-allowed' : 'bg-gray-50/50 text-gray-900'
                          }`}
                          placeholder={t('e.g. {example}', { example: storeCodeRule.example })}
                        />
                      </div>
                      <p className="mt-1.5 text-xs text-slate-500 leading-normal flex items-start gap-1">
                        <InformationCircleIcon className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                        <span>
                          {storeCodeFormat === 'LFS'
                            ? t("Please enter the store code provided by your Store Manager. If you don't have it, please ask the person who shared this screening link with you.")
                            : t("Please enter the 4-digit code provided by your Store Manager. If you don't have it, please ask the person who shared this screening link with you.")}
                        </span>
                      </p>
                    </div>
                  )}

                  {applyInfo?.cityListEnabled && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('City')} <span className="text-red-500">*</span>
                      </label>
                      <Listbox
                        value={form.cityId}
                        onChange={(val) => {
                          setForm((p) => ({ ...p, cityId: val }));
                          setError('');
                        }}
                        disabled={!!emailFromQuery}
                      >
                        {({ open }) => (
                          <div className="relative">
                            <Listbox.Button
                              className={`relative w-full cursor-default rounded-xl border border-gray-200 py-2.5 pl-10 pr-10 text-left text-sm focus:border-electric-blue focus:outline-none focus:ring-2 focus:ring-electric-blue/20 sm:py-3 transition-all ${
                                emailFromQuery ? 'bg-gray-100/70 text-gray-500 cursor-not-allowed' : 'bg-gray-50/50 text-gray-900 bg-white'
                              }`}
                            >
                              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                                <MapPinIcon className="h-5 w-5" />
                              </span>
                              <span className={`block truncate ${!form.cityId ? 'text-gray-400' : ''}`}>
                                {form.cityId
                                  ? applyInfo?.cities?.find(c => String(c.cityId) === form.cityId)?.city || t('Selected City')
                                  : t('Select your City')}
                              </span>
                              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                <ChevronDownIcon
                                  className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                                  aria-hidden="true"
                                />
                              </span>
                            </Listbox.Button>

                            <input
                              type="text"
                              tabIndex={-1}
                              className="absolute bottom-0 left-1/2 -translate-x-1/2 opacity-0 pointer-events-none w-full h-full"
                              required
                              value={form.cityId}
                              onChange={() => {}}
                            />

                            <Transition
                              as={React.Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                              afterLeave={() => setCitySearchQuery('')}
                            >
                              <Listbox.Options className="absolute z-[100] mt-1 max-h-60 w-full overflow-auto rounded-xl bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none border border-gray-100">
                                <div className="sticky top-0 z-10 bg-white px-3 py-2 border-b border-gray-100">
                                  <div className="relative">
                                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5">
                                      <svg className="h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                    <input
                                      type="text"
                                      className="block w-full rounded-md border-0 py-1.5 pl-9 pr-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-electric-blue sm:text-sm sm:leading-6"
                                      placeholder={t('Search city, state or region...')}
                                      value={citySearchQuery}
                                      onChange={(e) => setCitySearchQuery(e.target.value)}
                                      onClick={(e) => e.stopPropagation()}
                                      onKeyDown={(e) => {
                                        if (e.key === ' ') e.stopPropagation();
                                      }}
                                    />
                                  </div>
                                </div>
                                {filteredCities.length === 0 ? (
                                  <div className="relative cursor-default select-none py-4 px-4 text-gray-500 text-center">
                                    {t('No cities found.')}
                                  </div>
                                ) : (
                                  filteredCities.map((cityObj) => (
                                    <Listbox.Option
                                      key={cityObj.cityId}
                                      className={({ active }) =>
                                        `relative cursor-pointer select-none py-2.5 pl-4 pr-9 transition-colors ${
                                          active ? 'bg-electric-blue-50 text-electric-blue' : 'text-gray-900'
                                        }`
                                      }
                                      value={String(cityObj.cityId)}
                                    >
                                      {({ selected, active }) => (
                                        <>
                                            <span className={`block truncate ${selected ? 'font-semibold text-electric-blue' : 'font-normal'}`}>
                                              {cityObj.city}
                                            </span>
                                          {selected ? (
                                            <span
                                              className={`absolute inset-y-0 right-0 flex items-center pr-3 ${
                                                active ? 'text-electric-blue' : 'text-electric-blue'
                                              }`}
                                            >
                                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                            </span>
                                          ) : null}
                                        </>
                                      )}
                                    </Listbox.Option>
                                  ))
                                )}
                              </Listbox.Options>
                            </Transition>
                          </div>
                        )}
                      </Listbox>
                    </div>
                  )}

                  {consentNotice && (
                    // Ticked only by Agree inside the notice (which also records the 18+ confirmation on
                    // the server), so tapping the box or the link opens the notice instead of toggling.
                    <div className="flex items-start gap-3" data-testid="consent-notice-row">
                      <input type="checkbox" id="consent-notice" data-testid="consent-notice-checkbox"
                        checked={!!noticeConsentLogId} onChange={() => setNoticeOpen(true)}
                        className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-electric-blue focus:ring-electric-blue" aria-describedby="consent-notice-description" />
                      <label id="consent-notice-description" htmlFor="consent-notice" className="text-[11px] sm:text-xs text-gray-700 cursor-pointer select-none leading-snug">
                        {withNodes(t("I agree to {company}'s {notice} for processing my personal data"), {
                          company: companyName || t('the employer'),
                          notice: (
                            <button type="button" onClick={(e) => { e.preventDefault(); setNoticeOpen(true); }} data-testid="consent-notice-open"
                              className="font-semibold text-electric-blue hover:underline">
                              {t('Consent Notice')}
                            </button>
                          ),
                        })}
                        <span className="text-red-500">*</span>
                        {!noticeConsentLogId && <span className="block mt-0.5 text-gray-500">{t('Open the notice to read it and agree.')}</span>}
                      </label>
                    </div>
                  )}
                  <div className="flex items-start gap-3">
                    <input type="checkbox" id="consent" name="consent" data-testid="consent-checkbox" checked={form.consent} onChange={handleChange}
                      disabled={!!emailFromQuery && !consentNotice}
                      className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-electric-blue focus:ring-electric-blue disabled:opacity-60 disabled:cursor-not-allowed" aria-describedby="consent-description" />
                    <label id="consent-description" htmlFor="consent" className="text-[11px] sm:text-xs text-gray-700 cursor-pointer select-none leading-snug">
                      {withNodes(t('I agree to the TalentCrux {terms} and {privacy}'), {
                        terms: <a href="/terms" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="font-semibold text-electric-blue hover:underline">{t('Terms of Use')}</a>,
                        privacy: <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="font-semibold text-electric-blue hover:underline">{t('Privacy Policy')}</a>,
                      })}
                      <span className="text-red-500">*</span>
                    </label>
                  </div>
                  {error && <div id="basic-details-error" className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700" role="alert">{t(error)}</div>}
                  <button type="submit" disabled={!form.consent || (!!consentNotice && !noticeConsentLogId)}
                    data-testid="apply-start-btn"
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-electric-blue to-indigo-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-electric-blue/20 hover:shadow-xl hover:shadow-electric-blue/25 focus:outline-none focus:ring-2 focus:ring-electric-blue focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none hover:-translate-y-0.5 disabled:hover:translate-y-0 sm:mt-4 sm:py-3.5"
                    aria-disabled={!form.consent || (!!consentNotice && !noticeConsentLogId)}>
                    {getButtonText()} <ArrowRightIcon className="h-4 w-4" />
                  </button>
                </form>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <h2 className="text-base font-semibold text-gray-900">{t('Upload your resume')}</h2>
              <p className="mt-1 text-xs text-gray-600">{t('Upload a recent resume so we can understand your experience.')}</p>
              <form onSubmit={handleSubmit} className="mt-5 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('Resume (PDF, DOCX, JPG, PNG)')}<span className="text-red-500">*</span></label>
                  <label className="mt-1 flex cursor-pointer items-center justify-between rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-xs text-gray-600 hover:border-electric-blue hover:bg-electric-blue-50">
                    <span>{selectedResumeName ? <><span className="font-semibold">{t('Selected:')}</span> {selectedResumeName}</> : t('Click to choose file')}</span>
                    <input ref={resumeInputRef} type="file" name="resume" data-testid="resume-upload-input" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,image/jpeg,image/png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" required onChange={handleChange} className="sr-only" />
                  </label>
                </div>
                {error && <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{t(error)}</div>}
                <div className="flex items-center justify-end pt-1">
                  <button type="submit" disabled={submitting}
                    data-testid="resume-submit-btn"
                    className="inline-flex items-center rounded-lg bg-electric-blue px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-electric-blue-600 disabled:opacity-60 transition-colors">
                    {getButtonText()}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Fallback when applyInfo not loaded */}
        {isBasicDetailsView && !applyInfo && !applyInfoLoading && !linkInactive && (
          <div className="mt-6 bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-bold text-gray-900">{t('Screening')}</h2>
            <p className="mt-1.5 text-xs text-gray-600 sm:text-sm">{t('Screening ID:')} <span className="font-mono text-electric-blue">{screeningId}</span></p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScreeningApply;
